// ui/useEuchreEngine.js: owns the wasmoon engine + LuaGameState
// lifecycle for the real-game-mode (first wired up in M2.c-10c).
//
// Returns { game, status, error }. `game` is the LuaGameState handle
// once ready (null while loading or on error). `status` is one of
// 'idle' | 'loading' | 'ready' | 'error'.
//
// Side-effects:
//   • Loads cards.lua, state.lua, bot.lua, parity/serialize.lua via
//     ?raw imports, Vite bundles them as strings.
//   • Calls `game.startBots({heuristic_disclosure})` after attach so
//     the bot scheduler can ask the facade for decisions.
//
// The engine is created exactly once per mount. Settings that the
// engine reads at construction time (stick_the_dealer, game_target,
// heuristic_disclosure) are passed in via the `settings` argument; the
// hook re-creates the engine if any of them change.

import { useEffect, useState } from 'preact/hooks';
import { createLuaRuntime } from '../lua/runtime.js';
import { LuaGameState } from '../lua/game/lua_bridge.js';
import serializeSrc from '../lua/parity/serialize.lua?raw';
import cardsSrc     from '../lua/game/cards.lua?raw';
import stateSrc     from '../lua/game/state.lua?raw';
import botSrc       from '../lua/game/bot.lua?raw';

/**
 * @param {{
 *   stick_the_dealer: boolean,
 *   game_target: number,
 *   heuristic_disclosure: boolean,
 *   enabled?: boolean,
 * }} settings
 */
export function useEuchreEngine(settings) {
  const enabled = settings.enabled !== false;
  const [game, setGame] = useState(/** @type {LuaGameState|null} */ (null));
  const [status, setStatus] = useState(/** @type {'idle'|'loading'|'ready'|'error'} */ ('idle'));
  const [error, setError] = useState(/** @type {string|null} */ (null));

  useEffect(() => {
    if (!enabled) { setStatus('idle'); return; }
    let cancelled = false;
    let engineRef = /** @type {any} */ (null);
    let gameRef   = /** @type {LuaGameState|null} */ (null);

    setStatus('loading');
    setError(null);

    (async () => {
      try {
        const runtime = await createLuaRuntime();
        if (cancelled) { await runtime.engine.global.close(); return; }
        engineRef = runtime.engine;
        await engineRef.doString(`
          package.loaded['parity.serialize'] = (function() ${serializeSrc} end)()
          package.loaded['game.cards']       = (function() ${cardsSrc}     end)()
          package.loaded['game.state']       = (function() ${stateSrc}     end)()
          package.loaded['game.bot']         = (function() ${botSrc}       end)()
        `);
        if (cancelled) { await engineRef.global.close(); return; }

        const g = await LuaGameState.attach(engineRef, {
          stick_the_dealer: settings.stick_the_dealer,
          game_target:      settings.game_target,
        });
        if (cancelled) { g.dispose(); await engineRef.global.close(); return; }

        g.startBots({ heuristic_disclosure: settings.heuristic_disclosure });
        gameRef = g;
        setGame(g);
        setStatus('ready');
      } catch (err) {
        if (cancelled) return;
        setStatus('error');
        setError(err instanceof Error ? err.message : String(err));
      }
    })();

    return () => {
      cancelled = true;
      if (gameRef) {
        try { gameRef.dispose(); } catch { /* ignore */ }
      }
      if (engineRef) {
        try { engineRef.global.close(); } catch { /* ignore */ }
      }
      setGame(null);
    };
  }, [
    enabled,
    settings.stick_the_dealer,
    settings.game_target,
    settings.heuristic_disclosure,
  ]);

  return { game, status, error };
}
